import { useEffect, useState, ReactNode } from "react"

import { ContentObjectItem } from "@vertesia/common"
import { motion } from "framer-motion"
import { X } from "lucide-react"

import { DocumentSelectionSearchPanel } from "@/components/panels/selection/DocumentSelectionSearchPanel"
import { DOCUMENT_FACETS as facets } from "@/constants/facets"
import { Button } from "@ui/button"
import { DocumentSearchProvider } from "@/plugins/providers/document/DocumentSearchProvider"
import { useUserSession } from "@session/UserSession"

interface DocumentSelectionOverlayProps {
    children?: ReactNode
    trigger?: number
    onSelection?: (document: string) => void
    onObjSelection?: (document: ContentObjectItem) => void
    onMultipleSelection?: (documents: string[]) => void
    onObjMultipleSelection?: (documents: ContentObjectItem[]) => void
}
export function DocumentSelectionOverlay({ children, trigger, onSelection, onObjSelection, onMultipleSelection, onObjMultipleSelection }: Readonly<DocumentSelectionOverlayProps>) {
    const { project } = useUserSession()

    const [isOpen, setIsOpen] = useState(false)
    const informationMessage = `Click on the '+' icon to add ${onMultipleSelection ? "" : "a"} document${onMultipleSelection ? "(s)" : ""} to the selection`

    const handleOnSelection = (document: ContentObjectItem) => {
        if (onSelection) {
            onSelection(document.id)
            setIsOpen(false)
        }
        if (onObjSelection) {
            onObjSelection(document)
            setIsOpen(false)
        }
    }

    const handleOnMultipleSelection = (documents: ContentObjectItem[]) => {
        if (onMultipleSelection) {
            onMultipleSelection(documents.map((doc) => doc.id))
            setIsOpen(false)
        }
        if (onObjMultipleSelection) {
            onObjMultipleSelection(documents)
            setIsOpen(false)
        }
    }

    useEffect(() => {
        if (trigger) {
            setIsOpen(!isOpen)
        }
    }, [trigger])

    return (
        <div className="flex items-center justify-center">
            <div onClick={() => setIsOpen(true)} className="w-full align-left">
                {children}
            </div>

            {
                isOpen && (
                    <div className="z-50 fixed w-full inset-0 bg-opacity-50 flex justify-end top-0">
                        <motion.div
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "0%" }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="w-80 h-full w-full bg-white shadow-lg p-0 relative"
                        >
                            <DocumentSearchProvider facets={facets(project?.id || "")}>
                                <DocumentSelectionSearchPanel onSelection={handleOnSelection} onMultipleSelection={handleOnMultipleSelection} information={informationMessage}>
                                    <Button
                                        onClick={() => setIsOpen(false)}
                                        variant="outline"
                                        className='h-full'
                                    >
                                        <X />
                                    </Button>
                                </DocumentSelectionSearchPanel>
                            </DocumentSearchProvider>
                        </motion.div>
                    </div>
                )
            }
        </div>
    )
}